'use client'

import { MoveRightIcon } from 'lucide-react'
import React, { useMemo } from 'react'

import { useBlogFeedApi } from '@/api/useBlogFeedApi'

import { Button } from '../ui/Button'
import { cn } from '../ui/cn'
import { FeedPaginator } from '../ui_patterns/FeedPaginator'

interface BlogCardProps {
  title?: string
  description?: string
  imgUrl?: string
  href: string
}

const MAX_SUMMARY_CHARS = 200

const stripHtml = (html: string | undefined): string => {
  if (!html) return ''
  const temp = document.createElement('div')
  temp.innerHTML = html
  const text = (temp.textContent || temp.innerText || '').replace(/\s+/g, ' ').trim()
  return text.length > MAX_SUMMARY_CHARS ? `${text.substring(0, MAX_SUMMARY_CHARS - 3)}...` : text
}

/**
 * Ship&Play "Find Your Next Adventure" band. Playground ships a hardcoded
 * `posts` array; Kettle pulls the same data through the existing
 * `useBlogFeedApi` GraphQL hook so the section reflects the live shipsticks
 * blog RSS. Markup is a verbatim port of playground `BlogFeed`.
 *
 * Renders nothing while loading or when the feed is empty — the section
 * disappears cleanly instead of showing skeleton cards, which is the
 * playground's behavior for the empty case too.
 */
export const BlogFeed = (): React.ReactElement | null => {
  const { data, isLoading, isError } = useBlogFeedApi()
  const posts = useMemo<BlogCardProps[]>(
    () =>
      (data ?? []).map((item) => ({
        title: item.title,
        description: stripHtml(item.summary),
        imgUrl: item.thumbnail || item.image,
        href: item.url,
      })),
    [data],
  )

  if (isLoading || isError || posts.length === 0) return null

  return (
    <section className="snplay:my-24 snplay:overflow-hidden">
      <div className="snplay-bounds">
        <h1 className="snplay:text-4xl snplay:font-bold">
          Find Your Next <span className="snplay:text-primaryDark"> Adventure</span>
        </h1>
        <p className="snplay:mt-2 snplay:text-base snplay:text-ui-secondary">
          From trending destinations to insider tips, explore our blog to see what&rsquo;s new and
          next in travel.
        </p>
        <FeedPaginator prevLabel="Previous articles" nextLabel="Next articles">
          {posts.map((post) => (
            <BlogCard key={post.href} {...post} />
          ))}
        </FeedPaginator>
      </div>
    </section>
  )
}

const BlogCard = ({ title, description, imgUrl, href }: BlogCardProps): React.ReactElement => {
  return (
    <article className="snplay:w-84 snplay:shrink-0">
      <a
        href={href}
        title={title}
        target="_blank"
        rel="noreferrer"
        className="snplay-can-focus snplay-link snplay:block snplay:rounded-lg"
      >
        <img
          src={imgUrl}
          alt={title}
          className="snplay:block snplay:h-45 snplay:w-full snplay:rounded-lg snplay:object-cover"
        />
      </a>
      <h2 className="snplay:my-4 snplay:text-lg snplay:font-bold">
        <a
          href={href}
          title={title}
          target="_blank"
          rel="noreferrer"
          className="snplay-can-focus snplay-link snplay:block snplay:truncate snplay:no-underline"
        >
          {title}
        </a>
      </h2>
      <p
        className={cn(
          'snplay:mb-4 snplay:line-clamp-3 snplay:h-18',
          'snplay:text-sm snplay:text-ui-secondary',
        )}
      >
        {description}
      </p>
      <Button href={href} className="snplay:w-fit" target="_blank" rel="noreferrer">
        Continue reading <MoveRightIcon className="snplay:size-5" />
      </Button>
    </article>
  )
}
